import { BASE_VIEW_SCALE } from '../constants';

/**
 * Every distance the snapping engine measures, and every weight it draws with, in
 * screen pixels. Multiply through the helpers in ./snapScale to turn these into
 * viewBox user units for the current zoom level.
 *
 * The values are expressed as `<old user-unit value> / BASE_VIEW_SCALE` so the default
 * view behaves exactly as it did when these were fixed user-unit constants. Retune the
 * screen-pixel number itself, not the divisor.
 */

/**
 * How close a point has to come before it snaps. The user aims at this in screen
 * pixels, so it stays a constant number of pixels at every zoom level — previously a
 * fixed 5 user units, which meant a ~71px grab radius when fully zoomed in and a ~1.4px
 * one when fully zoomed out.
 */
export const SNAP_TOLERANCE_PX = 8 / BASE_VIEW_SCALE;

/**
 * How far a reference element's edges project past the element itself. Screen-relative
 * so guidelines reach roughly the same distance across the viewport at any zoom,
 * instead of extending several screens' worth off-canvas when zoomed in.
 */
export const SNAP_EXTENSION_DISTANCE_PX = 450 / BASE_VIEW_SCALE;

/**
 * Added to every line candidate's distance so that point candidates win ties. Without
 * it, a line lying through a snap point overwrites the point.
 */
export const SNAP_LINE_PENALTY_PX = 3 / BASE_VIEW_SCALE;

/** Candidates are collected from further out than they are allowed to snap from. */
export const SNAP_CANDIDATE_RADIUS_RATIO = 2.5;

/**
 * Two line candidates may resolve to an intersection this much further away than a
 * normal snap: the result is visually correct on both lines regardless of distance.
 */
export const SNAP_LINE_LINE_RATIO = 5;

/** "Lies on" test used when collecting the lines and points to render. */
export const SNAP_MATCH_EPSILON_RATIO = 0.1;

/**
 * The snap visuals are uncapped screen pixels — unlike handles, they are never aimed
 * at, so they should hold exactly one size on screen at every zoom level.
 */

/** Width and height of the × drawn at each end of a snapline and at each snap point. */
export const SNAP_MARKER_SIZE_PX = 7;
/** Weight of the × strokes. */
export const SNAP_MARKER_STROKE_WIDTH_PX = 1.2;
/** Weight of the snapline itself. */
export const SNAPLINE_STROKE_WIDTH_PX = 1.2;
/** Weight of the debug overlay's extended guidelines. */
export const DEBUG_SNAPLINE_STROKE_WIDTH_PX = 1;
